"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CreaseToSVG_1 = require("../io/CreaseToSVG");
const Crease_1 = require("./Crease");
const CreaseBuilderSegment_1 = require("./CreaseBuilderSegment");
const CreaseEdge_1 = require("./CreaseEdge");
const CreaseEdgeLoop_1 = require("./CreaseEdgeLoop");
const CreaseEdgeRef_1 = require("./CreaseEdgeRef");
const CreaseFace_1 = require("./CreaseFace");
const CreaseVertex_1 = require("./CreaseVertex");
/**
 * A helper class that builds a Crease object from an array of segments.
 */
class CreaseBuilder {
    /**
     * Constructs a CreaseBuilder object.
     * @param {CreaseBuilderSegment[]} segments The input segments.
     */
    constructor(segments) {
        this._vertices = [];
        this._edges = [];
        this._faces = [];
        // Check input.
        common_1.check.isArray('segments', segments, [1, Infinity], (name, segment) => {
            common_1.check.isInstanceOf(name, segment, CreaseBuilderSegment_1.CreaseBuilderSegment);
        });
        this._segments = segments;
        this._debug = false;
        this._stats = {};
    }
    /**
     * Returns true if debug output is enabled.
     */
    get debug() {
        return this._debug;
    }
    set debug(value) {
        this._debug = value;
    }
    /**
     * The Node.js file system.
     */
    get fs() {
        return this._fs;
    }
    set fs(value) {
        this._fs = value;
    }
    /**
     * After {@link CreaseBuilder.build} has been called, returns an object containing
     * statistics about the build process.
     * @readonly
     */
    get stats() {
        return this._stats;
    }
    /**
     * Builds a Crease object.
     * @param {number} distanceTolerance The maximum distance between two vertices (or intersection
     * points) that are considered equivalent.
     * @param {number} angleTolerance The maximum angle (in radians) between two lines that are
     * still considered parallel.
     * @param {ProgressCancelCallback} [progressCancelCallback] A callback function that is passed
     * the current progress (as a percent from 0 to 100) and returns true if the user wishes to
     * cancel the operation, false otherwise.
     * @returns {Crease[]} An array of Crease objects. The array is empty if the operation
     * was canceled.
     */
    build(distanceTolerance, angleTolerance, progressCancelCallback) {
        // Check input.
        common_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        common_1.check.isInRange('angleTolerance', angleTolerance, 0, Math.PI / 2);
        this._progressCancelCallback = progressCancelCallback;
        // Create vertices and edges.
        this._createVerticesAndEdgesFromSegments();
        this._checkConsistency(1);
        // Clean up.
        this._stats = {};
        this._stats.removeStrayVertices = this._removeStrayVertices();
        this._checkConsistency(2);
        this._stats.collapseNearbyVertices = this._collapseNearbyVertices(distanceTolerance);
        this._checkConsistency(3);
        this._stats.subdivideCrossingEdges = this._subdivideCrossingEdges(distanceTolerance, angleTolerance);
        if (this._stats.subdivideCrossingEdges.canceled) {
            return [];
        }
        this._checkConsistency(4);
        this._stats.removeRedundantVertices = this._removeRedundantVertices(angleTolerance);
        this._checkConsistency(5);
        // Sort the incident edges at each vertex into counterclockwise order.
        this._sortEdgesAroundVertices();
        // Construct edge loops using the vertex and edge information, then create faces from the
        // edge loops.
        const { clockwiseLoops, counterclockwiseLoops } = this._createEdgeLoops();
        this._createFaces(clockwiseLoops, counterclockwiseLoops);
        this._checkConsistency(6);
        // Delete edges that are not part of any face.
        this._stats.removeInvalidEdges = this._removeInvalidEdges();
        this._checkConsistency(7);
        // Mark edges that have only one adjacent face as boundary edges.
        this._stats.assignBoundaryEdges = this._assignBoundaryEdges();
        this._checkConsistency(8);
        // Split any remaining cut edges, turning them into boundary edges.
        this._stats.splitCuts = this._splitCuts();
        this._checkConsistency(9);
        // Split any vertices with more than two boundary edges.
        this._stats.splitBoundaryVertices = this._splitBoundaryVertices();
        this._checkConsistency(10);
        // After splitting boundary vertices, there may be redundant vertices (vertices with
        // only two incident edges that are collinear). We used to remove these redundant vertices,
        // but that can cause triangulation to fail because an identical copy of one of these
        // vertices on the split boundary looks like a T-junction to the triangulation code.
        // Construct a Crease object.
        const crease = new Crease_1.Crease(this._vertices, this._edges, this._faces);
        this._checkConsistency(11, crease);
        // Split into separate components.
        const creases = crease.splitComponents();
        // Get rid of temporary data.
        delete this._vertices;
        delete this._edges;
        delete this._faces;
        return creases;
    }
    /**
     * Populates _vertices and _edges from _segments.
     * @private
     */
    _createVerticesAndEdgesFromSegments() {
        this._vertices = [];
        this._edges = [];
        this._segments.forEach((segment) => {
            // Find or create the edge's first vertex.
            const vertex1 = this._findOrCreateVertex(segment.points[0]);
            // Find or create the edge's second vertex.
            const vertex2 = this._findOrCreateVertex(segment.points[segment.points.length - 1]);
            // Use the remaining points as the edge's control points.
            const controlPoints = segment.points.slice(1, -1);
            // Create a new edge.
            const edge = new CreaseEdge_1.CreaseEdge(this._edges.length, vertex1, vertex2, segment.assignment, controlPoints, segment.foldAngle);
            this._edges.push(edge);
            // Add an edge reference to each of the vertices.
            const edgeRef1 = new CreaseEdgeRef_1.CreaseEdgeRef(edge, false);
            vertex1.incidentEdges.push(edgeRef1);
            const edgeRef2 = new CreaseEdgeRef_1.CreaseEdgeRef(edge, true);
            vertex2.incidentEdges.push(edgeRef2);
        });
    }
    /**
     * Finds or creates a vertex with the given position.
     * @private
     * @param {Vector2} position The position of the vertex.
     * @returns {CreaseVertex} A vertex with the given position.
     */
    _findOrCreateVertex(position) {
        // See if a vertex already exists at the given position.
        let vertex = this._vertices.find(v => common_1.vec.equal(v.position, position));
        if (!vertex) {
            // Nope, create a new vertex.
            vertex = new CreaseVertex_1.CreaseVertex(this._vertices.length, position);
            this._vertices.push(vertex);
        }
        return vertex;
    }
    /**
     * Removes vertices that are not connected to any edges.
     * @private
     * @returns {BasicStats} Statistics about the operation.
     */
    _removeStrayVertices() {
        const verticesToDelete = this._vertices.filter(v => v.incidentEdges.length === 0);
        return this._deleteVertices(verticesToDelete);
    }
    /**
     * Merge nearby vertices.
     * @private
     * @param {number} distanceTolerance The tolerance used for detecting nearby vertices.
     * @returns {CollapseNearbyVerticesStats} Statistics about the operation.
     */
    _collapseNearbyVertices(distanceTolerance) {
        const vertexHash = {};
        const mapping = [];
        this._vertices.forEach((vertex, i) => {
            mapping.push(i);
            const p = vertex.position;
            const xr = distanceTolerance === 0 ? p[0] : Math.round(p[0] / distanceTolerance);
            const yr = distanceTolerance === 0 ? p[1] : Math.round(p[1] / distanceTolerance);
            const key = `${xr},${yr}`;
            const entry = vertexHash[key];
            if (entry) {
                entry.push(i);
            }
            else {
                vertexHash[key] = [i];
            }
        });
        this._vertices.forEach((vertex, i) => {
            const p = vertex.position;
            const xr = distanceTolerance === 0 ? p[0] : Math.round(p[0] / distanceTolerance);
            const yr = distanceTolerance === 0 ? p[1] : Math.round(p[1] / distanceTolerance);
            for (let x = xr - 1; x <= xr + 1; x++) {
                for (let y = yr - 1; y <= yr + 1; y++) {
                    const neighbors = vertexHash[`${x},${y}`];
                    if (neighbors === undefined) {
                        continue;
                    }
                    neighbors.forEach((vertIndex) => {
                        if (mapping[vertIndex] === mapping[i]) {
                            return;
                        }
                        const otherVertex = this._vertices[vertIndex];
                        const distance = common_1.vec.distance(p, otherVertex.position);
                        if (distance <= distanceTolerance) {
                            const oldMap = mapping[vertIndex];
                            mapping.forEach((map, k) => {
                                if (mapping[k] === oldMap) {
                                    mapping[k] = mapping[i];
                                }
                            });
                        }
                    });
                }
            }
        });
        // For each merged vertex, use the position of the vertex with the most edges.
        let verticesToDelete = [];
        this._vertices.forEach((vertex, i) => {
            // Find all vertices that are being merged with this one.
            const verticesToMerge = this._vertices.filter((v, j) => mapping[j] === i && j !== i);
            // Determine which vertex has the most incident edges.
            const vertexWithMostEdges = verticesToMerge.reduce((previous, current) => {
                if (current.incidentEdges.length > previous.incidentEdges.length) {
                    return current;
                }
                return previous;
            }, vertex);
            // Use the position of the vertex with the most edges.
            vertex._setPosition(vertexWithMostEdges.position);
            // Modify edges incident to the merged vertices.
            verticesToMerge.forEach((mergedVertex) => {
                mergedVertex.incidentEdges.forEach((edgeRef) => {
                    // Switch the edge's vertex.
                    const { edge } = edgeRef;
                    edge._replaceVertex(mergedVertex, vertex);
                    // Add the edge to the vertex we're keeping.
                    vertex.incidentEdges.push(edgeRef);
                });
            });
            verticesToDelete = verticesToDelete.concat(verticesToMerge);
        });
        const { count } = this._deleteVertices(verticesToDelete);
        // Clean up.
        const removeLoopEdges = this._removeLoopEdges();
        const removeDuplicateEdges = this._removeDuplicateEdges(distanceTolerance);
        return { count, removeLoopEdges, removeDuplicateEdges };
    }
    /**
     * Removes edges that start and end at the same vertex.
     * @private
     * @returns {BasicStats} Statistics about the operation.
     */
    _removeLoopEdges() {
        const edgesToDelete = [];
        this._edges.forEach((edge) => {
            if (edge.vertex1 === edge.vertex2) {
                edgesToDelete.push(edge);
            }
        });
        return this._deleteEdges(edgesToDelete);
    }
    /**
     * Removes duplicate edges.
     * @private
     * @param {number} distanceTolerance The tolerance used for detecting nearby vertices.
     * @returns {BasicStats} Statistics about the operation.
     */
    _removeDuplicateEdges(distanceTolerance) {
        // Define a helper function that compares two lists of points.
        const arePointsCloseEnough = (points1, points2) => points1.length === points2.length
            && points1.every((p, i) => common_1.vec.distance(p, points2[i]) < distanceTolerance);
        // Compare each edge to each earlier edge.
        const edgesToDelete = [];
        for (let i = 1; i < this._edges.length; i++) {
            const edge1 = this._edges[i];
            const reversedControlPoints = edge1.controlPoints.slice().reverse();
            for (let j = 0; j < i; j++) {
                const edge2 = this._edges[j];
                // See if edge1 and edge2 connect the same vertices and have equal control points.
                if ((edge1.vertex1 === edge2.vertex1 && edge1.vertex2 === edge2.vertex2
                    && arePointsCloseEnough(edge1.controlPoints, edge2.controlPoints))
                    || (edge1.vertex1 === edge2.vertex2 && edge1.vertex2 === edge2.vertex1
                        && arePointsCloseEnough(reversedControlPoints, edge2.controlPoints))) {
                    // The two edges match geometrically.
                    if (edge1.assignment === edge2.assignment) {
                        // The edges have the same assignment. Delete one.
                        edgesToDelete.push(edge1);
                    }
                    else if (edge1.assignment === 'C') {
                        // Prioritize C edges.
                        edgesToDelete.push(edge2);
                    }
                    else if (edge2.assignment === 'C') {
                        // Prioritize C edges.
                        edgesToDelete.push(edge1);
                    }
                    else if (edge1.assignment === 'B') {
                        // Prioritize B edges.
                        edgesToDelete.push(edge2);
                    }
                    else if (edge2.assignment === 'B') {
                        // Prioritize B edges.
                        edgesToDelete.push(edge1);
                    }
                    else {
                        // No op: the edges have different assignments, so keep both.
                    }
                }
            }
        }
        return this._deleteEdges(edgesToDelete);
    }
    /**
     * Deletes the specified vertices.
     * @private
     * @param {CreaseVertex[]} verticesToDelete An array of vertices to delete.
     * @returns {BasicStats} Statistics about the operation.
     */
    _deleteVertices(verticesToDelete) {
        // Remove vertices.
        let count = 0;
        verticesToDelete.forEach((vertex) => {
            if (common_1.utils.removeItemFromArray(this._vertices, vertex)) {
                count += 1;
            }
        });
        // Reindex.
        this._vertices.forEach((vertex, i) => {
            vertex._setIndex(i);
        });
        return { count };
    }
    /**
     * Deletes the specified edges.
     * @private
     * @param {CreaseEdge[]} edgesToDelete An array of edges to delete.
     * @returns {BasicStats} Statistics about the operation.
     */
    _deleteEdges(edgesToDelete) {
        // Remove edges.
        let count = 0;
        edgesToDelete.forEach((edge) => {
            // Remove the edge.
            if (common_1.utils.removeItemFromArray(this._edges, edge)) {
                count += 1;
                // Disconnect the edge from its first vertex.
                const index1 = edge.vertex1.incidentEdges.findIndex(r => r.edge === edge);
                if (index1 >= 0) {
                    edge.vertex1.incidentEdges.splice(index1, 1);
                }
                // Disconnect the edge from its second vertex.
                const index2 = edge.vertex2.incidentEdges.findIndex(r => r.edge === edge);
                if (index2 >= 0) {
                    edge.vertex2.incidentEdges.splice(index2, 1);
                }
                // If we've already constructed faces, remove the current edge from any face loops.
                if (this._faces) {
                    this._faces.forEach((face) => {
                        face.edgeLoops.forEach((loop) => {
                            loop.removeEdgeRefs(edge);
                        });
                    });
                }
            }
        });
        // Reindex.
        this._edges.forEach((edge, i) => {
            edge._setIndex(i);
        });
        return { count };
    }
    /**
     * Splits edges at intersection points.
     * @private
     * @param {number} distanceTolerance The tolerance used when detecting edge intersections.
     * @param {number} angleTolerance The maximum angle (in radians) between two lines that are
     * still considered parallel.
     * @returns {SubdivideCrossingEdgesStats} Statistics about the operation.
     */
    _subdivideCrossingEdges(distanceTolerance, angleTolerance) {
        // When computing intersections, use a slightly smaller tolerance than we were passed, so
        // that later _collapseNearbyVertices will be able to clean up approximate intersections.
        const reducedDistanceTolerance = 0.98 * distanceTolerance;
        const totalProgress = this._edges.length * (this._edges.length - 1) / 2;
        let numIntersections = 0;
        let currentProgress = 0;
        let lastPercentProgress = 0;
        for (let i = 1; i < this._edges.length; i++) {
            for (let j = 0; j < i; j++) {
                // Get the Bezier control points for edges i and j.
                const edgeI = this._edges[i];
                const edgeJ = this._edges[j];
                const edgePoints = [edgeI.allPoints, edgeJ.allPoints];
                // Compute all intersection points of these two edges.
                const intersections = common_1.geom.getIntersections(edgePoints[0], edgePoints[1], reducedDistanceTolerance, angleTolerance);
                // Do two passes, first subdividing edge i at all intersection points, then
                // subdividing edge j at all intersection points.
                for (let e = 0; e < 2; e++) {
                    // Choose edge i or edge j.
                    let splitEdge = e === 0 ? edgeI : edgeJ;
                    // Filter the intersections to those that are in the interior of
                    // the current edge, then sort them by their parameter value.
                    const sortedIntersections = intersections.filter(a => a.params[e] !== 0
                        && a.params[e] !== 1);
                    sortedIntersections.sort((a, b) => a.params[e] - b.params[e]);
                    // Subdivide the current edge at each intersection point.
                    /* eslint-disable-next-line no-loop-func */
                    sortedIntersections.forEach((intersection) => {
                        // Create a new vertex at the intersection point.
                        const position = intersection.positions[e];
                        const newVertex = new CreaseVertex_1.CreaseVertex(this._vertices.length, position);
                        this._vertices.push(newVertex);
                        numIntersections += 1;
                        // Modify the existing edge to end at the new vertex.
                        const oldVertex = splitEdge.vertex2;
                        splitEdge._replaceVertex(oldVertex, newVertex);
                        newVertex.incidentEdges.push(new CreaseEdgeRef_1.CreaseEdgeRef(splitEdge, true));
                        // Subdivide the Bezier curve for this edge.
                        const { left, right } = common_1.geom.splitBezier(edgePoints[e], intersection.params[e]);
                        splitEdge._setControlPoints(left.slice(1, -1));
                        // Create a new edge from the new vertex to the former end vertex.
                        const newEdge = new CreaseEdge_1.CreaseEdge(this._edges.length, newVertex, oldVertex, splitEdge.assignment, right.slice(1, -1), splitEdge.foldAngle);
                        this._edges.push(newEdge);
                        newVertex.incidentEdges.push(new CreaseEdgeRef_1.CreaseEdgeRef(newEdge, false));
                        const edgeRef = oldVertex.incidentEdges.find(r => r.edge === splitEdge);
                        if (!edgeRef) {
                            throw new Error('Could not find edge reference matching split edge.');
                        }
                        edgeRef._replaceEdge(newEdge);
                        // The next intersection point (if any) should subdivide the new edge.
                        splitEdge = newEdge;
                    });
                }
                // Report progress.
                currentProgress += 1;
                if (this._progressCancelCallback) {
                    const percentProgress = Math.round(100 * currentProgress / totalProgress);
                    if (percentProgress > lastPercentProgress) {
                        lastPercentProgress = percentProgress;
                        const shouldCancel = this._progressCancelCallback(percentProgress);
                        if (shouldCancel) {
                            return { canceled: true };
                        }
                    }
                }
            }
        }
        // Clean up by merging nearby vertices and removing duplicate edges.
        const collapseNearbyVertices = this._collapseNearbyVertices(distanceTolerance);
        return { count: numIntersections, collapseNearbyVertices, canceled: false };
    }
    /**
     * Removes redundant vertices (vertices that subdivide an edge and connect to no other edges).
     * @private
     * @param {number} angleTolerance The tolerance used to detect collinearity of adjacent edges.
     * @returns {RemoveRedundantVerticesStats} Statistics about the operation.
     */
    _removeRedundantVertices(angleTolerance) {
        // Figure out which vertices we want to delete.
        const collinearTolerance = 1 - Math.cos(angleTolerance);
        const verticesToDelete = [];
        this._vertices.forEach((vertex) => {
            // Skip vertices that have anything other than two incident edges.
            if (vertex.incidentEdges.length !== 2) {
                return;
            }
            // Get the positions of the neighboring vertices.
            const edgeRef1 = vertex.incidentEdges[0];
            const edgeRef2 = vertex.incidentEdges[1];
            const position1 = edgeRef1.vertex2.position;
            const position2 = edgeRef2.vertex2.position;
            // See if the two incident edges have identical assignments, have identical fold
            // angles, are straight, and are collinear.
            if (edgeRef1.assignment === edgeRef2.assignment
                && edgeRef1.foldAngle === edgeRef2.foldAngle
                && edgeRef1.controlPoints.length === 0
                && edgeRef2.controlPoints.length === 0
                && common_1.geom.arePointsCollinear(vertex.position, position1, position2, collinearTolerance)) {
                // Yes, we can safely delete this vertex.
                verticesToDelete.push(vertex);
            }
        });
        // Modify existing edges to skip over the vertices we want to delete.
        const edgesToDelete = [];
        verticesToDelete.forEach((vertex) => {
            // Get the incident edges and a neighboring vertex.
            const edgeRef1 = vertex.incidentEdges[0];
            const edgeRef2 = vertex.incidentEdges[1];
            const neighbor2 = edgeRef2.vertex2;
            // Change the first edge (from neighbor1 to vertex) to skip the current vertex and
            // connect directly to neighbor2.
            const { edge } = edgeRef1;
            edge._replaceVertex(vertex, neighbor2);
            // Mark the second edge for deletion.
            edgesToDelete.push(edgeRef2.edge);
            // Replace the edge reference in neighbor2 with the edge reference we're keeping.
            const neighbor2Index = neighbor2.incidentEdges.findIndex(r => r.edge === edgeRef2.edge);
            neighbor2.incidentEdges.splice(neighbor2Index, 1, edgeRef1);
            // Remove incident edges from the current vertex.
            vertex.incidentEdges.length = 0;
        });
        // Delete the unneeded vertices and edges.
        const deleteVertices = this._deleteVertices(verticesToDelete);
        const deleteEdges = this._deleteEdges(edgesToDelete);
        return {
            deleteVertices,
            deleteEdges,
        };
    }
    /**
     * Sorts the incident edges of each vertex into counterclockwise order.
     * @private
     */
    _sortEdgesAroundVertices() {
        this._vertices.forEach((vertex) => {
            vertex.sortIncidentEdges();
        });
    }
    /**
     * Creates edge loops from _vertices and _edges.
     * @private
     * @returns {{ clockwiseLoops: CreaseEdgeRef[][], counterclockwiseLoops: CreaseEdgeRef[][] }}
     * An array of clockwise edge loops and an array of counterclockwise edge loops.
     */
    _createEdgeLoops() {
        const clockwiseLoops = [];
        const counterclockwiseLoops = [];
        const visitedEdgeRefs = new Set();
        this._vertices.forEach((vertex) => {
            vertex.incidentEdges.forEach((edgeRef) => {
                // Create a new edge loop whenever we encounter an edge that hasn't been visited.
                if (!visitedEdgeRefs.has(edgeRef)) {
                    const edgeLoop = CreaseBuilder._createEdgeLoop(edgeRef, visitedEdgeRefs);
                    // Use the signed area of the polygon enclosed by the loop to categorize the
                    // edge loop as clockwise or counterclockwise. We include the curve control
                    // points here because they help in cases where a hole is formed from one
                    // straight edge and one curved edge.
                    const points = edgeLoop.reduce((p, e) => [...p, e.vertex1.position, ...e.controlPoints], []);
                    const signedArea = common_1.geom.getSignedArea(points);
                    if (common_1.geom.polygonOrientationFromSignedArea(signedArea) > 0) {
                        counterclockwiseLoops.push(edgeLoop);
                    }
                    else {
                        clockwiseLoops.push(edgeLoop);
                    }
                }
            });
        });
        return { clockwiseLoops, counterclockwiseLoops };
    }
    /**
     * Creates faces from clockwise and counterclockwise loops of edge references.
     * @private
     * @param {CreaseEdgeRef[][]} clockwiseLoops The clockwise edge loops.
     * @param {CreaseEdgeRef[][]} counterclockwiseLoops The counterclockwise edge loops.
     */
    _createFaces(clockwiseLoops, counterclockwiseLoops) {
        // Start by making each counterclockwise loop the outer boundary of a face.
        const facesEdgeLoops = counterclockwiseLoops.map(loop => [loop]);
        // Add each clockwise loop (representing a hole) to the face that contains it (if any).
        clockwiseLoops.forEach((clockwiseLoop) => {
            const clockwisePoints = clockwiseLoop.map(edgeRef => edgeRef.vertex1.position);
            let parentCount = 0;
            facesEdgeLoops.forEach((faceEdgeLoops) => {
                // Use raycasting to see if the clockwise loop is within the current
                // face's outer loop.
                const outerLoop = faceEdgeLoops[0];
                const outerLoopPoints = outerLoop.map(edgeRef => edgeRef.vertex1.position);
                if (common_1.geom.isPolygonInsidePolygon(clockwisePoints, outerLoopPoints)) {
                    // Include the hole in the face.
                    faceEdgeLoops.push(clockwiseLoop);
                    parentCount += 1;
                }
            });
            // If we added the hole to more than one face, throw an exception. This code will need
            // to be made more general if we ever encounter a hole within a face that lies within
            // a hole of another face.
            if (parentCount > 1) {
                throw new Error('Encountered a hole that is nested within multiple faces.');
            }
        });
        // Punch out holes: eliminate any faces that are completely surrounded by boundary or cut
        // edges and are adjacent to at least one other face.
        const filteredFacesEdgeLoops = facesEdgeLoops.filter(faceEdgeLoops => 
        // For faces we keep, either the outer edge loop has a non-boundary/non-cut edge:
        faceEdgeLoops[0].some(edgeRef => edgeRef.assignment !== 'B'
            && edgeRef.assignment !== 'C')
            // Or every edge in the outer loop has no adjacent face:
            || faceEdgeLoops[0].every(edgeRef => !facesEdgeLoops.find(f => f !== faceEdgeLoops && f.some(loop => loop.some(e => e.edge === edgeRef.edge)))));
        // Create the faces.
        this._faces = filteredFacesEdgeLoops.map((faceEdgeLoops, i) => new CreaseFace_1.CreaseFace(i, faceEdgeLoops.map(edgeRefs => new CreaseEdgeLoop_1.CreaseEdgeLoop(edgeRefs))));
    }
    /**
     * Removes all edges that are not connected to any faces.
     * @private
     * @returns {RemoveInvalidEdgesStats} Statistics about the operation.
     */
    _removeInvalidEdges() {
        // Find edges that do not appear in any face loop.
        const edgesToDelete = this._edges.filter(edge => this._faces.every(face => face.edgeLoops.every(loop => loop.edgeRefs.every(edgeRef => edgeRef.edge !== edge))));
        // Delete the edges, then remove any unconnected vertices.
        const { count } = this._deleteEdges(edgesToDelete);
        const removeStrayVertices = this._removeStrayVertices();
        return { count, removeStrayVertices };
    }
    /**
     * Marks all edges that are adjacent to only one face as boundary edge.
     * @private
     * @returns {BasicStats} Statistics about the operation.
     */
    _assignBoundaryEdges() {
        // Count the number of edges that we label as boundary edges.
        const count = common_1.utils.countIf(this._edges, edge => {
            // Sum up the number of times a face refers to the current edge.
            const faceCount = common_1.utils.sum(this._faces, face => common_1.utils.sum(face.edgeLoops, loop => common_1.utils.countIf(loop.edgeRefs, edgeRef => edgeRef.edge === edge)));
            // Fewer than two references means it's a boundary edge.
            if (faceCount < 2) {
                edge.assignment = 'B';
                return true;
            }
            return false;
        });
        return { count };
    }
    /**
     * Duplicates the edges along cuts, converting them to boundaries.
     * @private
     * @returns {BasicStats} Statistics about the operation.
     */
    _splitCuts() {
        // Split each cut edge into a pair of duplicate boundary edges.
        const cutEdges = this._edges.filter(edge => edge.assignment === 'C');
        let count = 0;
        cutEdges.forEach((edge) => {
            const v1 = edge.vertex1;
            const v2 = edge.vertex2;
            const index1 = v1.incidentEdges.findIndex(edgeRef => edgeRef.edge === edge);
            const index2 = v2.incidentEdges.findIndex(edgeRef => edgeRef.edge === edge);
            // Create a new edge.
            const controlPoints = edge.controlPoints.slice().reverse();
            const newEdge = new CreaseEdge_1.CreaseEdge(this._edges.length, v2, v1, 'B', controlPoints, null);
            this._edges.push(newEdge);
            count += 1;
            // Add new edge references to v1 and v2.
            const newEdgeRef1 = new CreaseEdgeRef_1.CreaseEdgeRef(newEdge, true);
            const newEdgeRef2 = new CreaseEdgeRef_1.CreaseEdgeRef(newEdge, false);
            v1.incidentEdges.splice(index1, 0, newEdgeRef1);
            v2.incidentEdges.splice(index2 + 1, 0, newEdgeRef2);
            // Replace the edge reference in f2 (the one in f1 stays the same).
            this._faces.forEach(f => f.edgeLoops.forEach(loop => loop.edgeRefs.forEach((edgeRef, i) => {
                if (edgeRef.edge === edge && edgeRef.isReversed) {
                    loop.edgeRefs.splice(i, 1, newEdgeRef2);
                }
            })));
            // Mark the old edge as a boundary.
            edge.assignment = 'B';
        });
        return { count };
    }
    /**
     * Splits each vertex with more than two boundary edges into multiple vertices.
     * @private
     * @returns {BasicStats} Statistics about the operation.
     */
    _splitBoundaryVertices() {
        let count = 0;
        this._vertices.forEach((vertex) => {
            // See how many boundary edges are incident to the current vertex.
            const edgeRefs = vertex.incidentEdges;
            const boundaryEdgeCount = common_1.utils.countIf(edgeRefs, edgeRef => edgeRef.assignment === 'B');
            if (boundaryEdgeCount % 2 !== 0) {
                // No vertex should have an odd number of boundary edges.
                throw new Error(`Vertex ${vertex.index} has an odd number (${boundaryEdgeCount}) of incident boundary edges.`);
            }
            // If there are more than two, we'll split this vertex.
            if (boundaryEdgeCount > 2) {
                const isInitialBoundaryEdge = (edgeRef) => edgeRef.assignment === 'B'
                    && this._faces.some(face => face.edgeLoops.some(loop => loop.edgeRefs.some(er => er.edge === edgeRef.edge && er.isReversed === edgeRef.isReversed)));
                // Find the first boundary edge with a face on its left.
                const firstBoundaryIndex = edgeRefs.findIndex(edgeRef => isInitialBoundaryEdge(edgeRef));
                if (firstBoundaryIndex < 0) {
                    throw new Error(`Vertex ${vertex.index} has ${boundaryEdgeCount} boundary edges, but none with a face on the left.`);
                }
                // Reorder the incident edges so they start with the first boundary edge.
                edgeRefs.splice(0, 0, ...edgeRefs.splice(firstBoundaryIndex));
                // Split incident edges up into groups that are connected by faces.
                const edgeRefGroups = [];
                edgeRefs.forEach((edgeRef) => {
                    if (isInitialBoundaryEdge(edgeRef)) {
                        edgeRefGroups.push([edgeRef]);
                    }
                    else {
                        edgeRefGroups[edgeRefGroups.length - 1].push(edgeRef);
                    }
                });
                // Create a new vertex for each group of edges after the first one.
                edgeRefGroups.forEach((edgeRefGroup, i) => {
                    if (edgeRefGroup.length < 2) {
                        throw new Error(`Vertex ${vertex.index} has an edge group with too few edges.`);
                    }
                    if (i > 0) {
                        // Create a new vertex.
                        const newVertex = new CreaseVertex_1.CreaseVertex(this._vertices.length, vertex.position);
                        newVertex._setIncidentEdges(edgeRefGroup);
                        this._vertices.push(newVertex);
                        count += 1;
                        // Change each edge in the group to connect to the new vertex.
                        edgeRefGroup.forEach((edgeRef) => {
                            const { edge } = edgeRef;
                            edge._replaceVertex(vertex, newVertex);
                        });
                    }
                });
                // Remove incident edges that have been moved from the old vertex to new ones.
                edgeRefs.splice(edgeRefGroups[0].length);
            }
        });
        return { count };
    }
    /**
     * Creates an edge loop by following a chain of edges starting from an initial edge.
     * @private
     * @param {CreaseEdgeRef} firstEdgeRef The initial edge of the loop.
     */
    static _createEdgeLoop(firstEdgeRef, visitedEdgeRefs) {
        const edgeLoop = [];
        let currentEdgeRef = firstEdgeRef;
        while (!visitedEdgeRefs.has(currentEdgeRef)) {
            // Add the current edge to the loop and mark it as visited.
            edgeLoop.push(currentEdgeRef);
            visitedEdgeRefs.add(currentEdgeRef);
            // Consider the set of edges incident to the second vertex of the current edge.
            const v2Edges = currentEdgeRef.vertex2.incidentEdges;
            const v2EdgeCount = v2Edges.length;
            // Figure out the next adjacent edge.
            /* eslint-disable-next-line no-loop-func */
            const index = v2Edges.findIndex(e => e.edge === currentEdgeRef.edge);
            currentEdgeRef = v2Edges[(index + v2EdgeCount - 1) % v2EdgeCount];
        }
        return edgeLoop;
    }
    /**
     * Checks for internal consistency, throwing an exception if anything is wrong.
     * @private
     * @param {number} stepNumber A unique number used for debug output.
     */
    _checkConsistency(stepNumber, crease) {
        // Save SVG at each step if the debug flag is set.
        if (this.debug && this.fs) {
            this._saveDebug(this.fs, stepNumber);
        }
        // Get the number of vertices, edges, and faces.
        const vertexCount = this._vertices ? this._vertices.length : 0;
        const edgeCount = this._edges ? this._edges.length : 0;
        const faceCount = this._faces ? this._faces.length : 0;
        // Check vertices.
        if (this._vertices) {
            common_1.check.isArray('_vertices', this._vertices, vertexCount, (name, vertex, i) => {
                common_1.check.isInstanceOf(name, vertex, CreaseVertex_1.CreaseVertex);
                common_1.check.equals(`${name}.index`, vertex.index, i);
                common_1.check.isArray(`${name}.incidentEdges`, vertex.incidentEdges, [0, edgeCount], (edgeName, edge) => {
                    common_1.check.equals(`${edgeName}.vertex1`, edge.vertex1, vertex);
                });
                common_1.check.isArray(`${name}.incidentFaces`, vertex.incidentFaces, undefined, (faceName, face) => {
                    if (!face.edgeLoops.some((loop) => loop.edgeRefs.some(r => r.vertex1 === vertex))) {
                        throw new Error(`Expected some vertex of face ${faceName} to match current vertex.`);
                    }
                });
            });
        }
        // Check edges.
        if (this._edges) {
            common_1.check.isArray('_edges', this._edges, edgeCount, (name, edge, i) => {
                common_1.check.isInstanceOf(name, edge, CreaseEdge_1.CreaseEdge);
                common_1.check.equals(`${name}.index`, edge.index, i);
                const foundInVertex1 = edge.vertex1.incidentEdges.some((er) => er.edge === edge);
                common_1.check.equals(`${name} foundInVertex1`, foundInVertex1, true);
                const foundInVertex2 = edge.vertex2.incidentEdges.some((er) => er.edge === edge);
                common_1.check.equals(`${name} foundInVertex2`, foundInVertex2, true);
                if (edge.leftFace) {
                    const foundInLeftFace = edge.leftFace.edgeLoops.some((loop) => loop.edgeRefs.some(er => er.edge === edge));
                    common_1.check.equals(`${name} foundInLeftFace`, foundInLeftFace, true);
                }
                if (edge.rightFace) {
                    const foundInRightFace = edge.rightFace.edgeLoops.some((loop) => loop.edgeRefs.some(er => er.edge === edge));
                    common_1.check.equals(`${name} foundInRightFace`, foundInRightFace, true);
                }
            });
        }
        // Check faces.
        if (this._faces) {
            common_1.check.isArray('_faces', this._faces, faceCount, (name, face, i) => {
                common_1.check.isInstanceOf(name, face, CreaseFace_1.CreaseFace);
                common_1.check.equals(`${name}.index`, face.index, i);
                common_1.check.isArray(`${name}.edgeLoops`, face.edgeLoops, [1, Infinity], (loopName, loop) => {
                    common_1.check.isArray(`${loopName}.edgeRefs`, loop.edgeRefs, [2, edgeCount], (erName, er) => {
                        if (crease) {
                            // After constructing a Crease object, edges should have
                            // incident faces.
                            common_1.check.equals(`${erName}.leftFace`, er.leftFace, face);
                        }
                        else {
                            // Before constructing a Crease object, edge references should
                            // be shared between vertices and faces.
                            common_1.check.isAmong(erName, er, er.vertex1.incidentEdges);
                        }
                    });
                    // // TODO: need to add support for curved edges in geom.getSignedArea first
                    // if (crease) {
                    //     // After constructing a Crease object, faces should be cc with cw holes.
                    //     const signedArea = geom.getSignedArea(loop.vertices.map(v =>
                    //         v.position));
                    //     const orientation = geom.polygonOrientationFromSignedArea(signedArea);
                    //     if (j === 0) {
                    //         // Outer boundary of face.
                    //         check.equals(`${loopName}[${j}].orientation`, orientation, 1);
                    //     } else {
                    //         check.notEquals(`${loopName}[${j}].orientation`, orientation, 1);
                    //     }
                    // }
                });
            });
        }
    }
    /**
     * Saves the current state in an SVG file for debugging purposes.
     * @private
     * @param {WritableFileSystem} fs The Node.js file system.
     * @param {number} stepNumber A unique step number to use in the SVG file name.
     */
    _saveDebug(fs, stepNumber) {
        // Create a mock object that supports just enough of the Crease class's API to satisfy
        // CreaseToSVG.
        const creaseMock = {
            vertices: this._vertices,
            edges: this._edges,
            faces: this._faces || [],
            components: [],
            copy: () => creaseMock,
            getBounds: () => {
                const vertexPoints = this._vertices.map(vertex => vertex.position);
                const allPoints = this._edges.reduce((points, edge) => points.concat(edge.controlPoints), vertexPoints);
                return common_1.geom.getBounds(allPoints);
            },
            scale: () => { },
            translate: () => { },
        };
        // Use CreaseToSVG to save the current state in an SVG file.
        const options = { includeLabels: true, includeDualGraph: true };
        CreaseToSVG_1.CreaseToSVG.saveSVG(fs, `CreaseBuilder-debug-${stepNumber}.svg`, creaseMock, options);
    }
}
exports.CreaseBuilder = CreaseBuilder;
//# sourceMappingURL=CreaseBuilder.js.map